import { useEffect, useState } from "react";
import axios from "../api/axios";
import { toast } from "react-toastify";

function TeamModule() {
  const [teams, setTeams] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    id: null,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    try {
      const res = await axios.get("/api/teams");
      setTeams(res.data);
    } catch (err) {
      console.error("Failed to fetch teams", err);
      toast.error("Failed to fetch teams");
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (formData.id) {
        await axios.put(`/api/teams/${formData.id}`, formData);
        toast.success("Team updated successfully");
      } else {
        await axios.post("/api/teams", formData);
        toast.success("Team created successfully");
      }
      setFormData({ name: "", description: "", id: null });
      fetchTeams();
    } catch (err) {
      console.error("Failed to save team", err);
      toast.error("Failed to save team");
    }
    setLoading(false);
  };

  const handleEdit = (team) => {
    setFormData({ ...team });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this team?")) return;
    try {
      await axios.delete(`/api/teams/${id}`);
      toast.success("Team deleted");
      fetchTeams();
    } catch (err) {
      console.error("Failed to delete team", err);
      toast.error("Delete failed");
    }
  };

  return (
    <div className="p-10 bg-indigo-50 min-h-screen">
      <div className="max-w-4xl mx-auto space-y-10">
        <header className="text-center">
          <h2 className="text-4xl font-extrabold text-gray-900">
            Team Management
          </h2>
          <p className="mt-2 text-gray-600">
            Create, edit, and organize your teams with ease
          </p>
        </header>

        <form
          onSubmit={handleSubmit}
          className="bg-white ring-1 ring-gray-200 shadow-md rounded-3xl p-10 space-y-8"
        >
          <h3 className="text-2xl font-semibold text-gray-800">
            {formData.id ? "Edit Team" : "Create New Team"}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Name
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-5 py-3 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                required
              />
            </div>
            <div className="space-y-3 md:col-span-2">
              <label className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-5 py-3 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                rows={4}
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-6">
            <button
              type="submit"
              disabled={loading}
              className="bg-indigo-600 text-white px-8 py-3 rounded-full shadow-lg hover:bg-indigo-700 transition disabled:opacity-50"
            >
              {loading
                ? "Saving..."
                : formData.id
                ? "Update Team"
                : "Create Team"}
            </button>
            {formData.id && (
              <button
                type="button"
                onClick={() =>
                  setFormData({ name: "", description: "", id: null })
                }
                className="bg-gray-200 text-gray-700 px-8 py-3 rounded-full hover:bg-gray-300 transition"
              >
                Cancel
              </button>
            )}
          </div>
        </form>

        <section>
          <h3 className="text-2xl font-semibold text-gray-800 mb-6">
            Existing Teams
          </h3>
          {teams.length === 0 ? (
            <p className="text-center text-gray-500">No teams found.</p>
          ) : (
            <ul className="space-y-4">
              {teams.map((team) => (
                <li
                  key={team.id}
                  className="group bg-white ring-1 ring-gray-200 rounded-2xl p-6 flex flex-col md:flex-row justify-between items-start md:items-center hover:shadow-lg transition"
                >
                  <div className="space-y-1">
                    <p className="text-xl font-medium text-gray-900">
                      {team.name}
                    </p>
                    <p className="text-gray-600">{team.description}</p>
                  </div>
                  <div className="mt-4 md:mt-0 flex space-x-4">
                    <button
                      onClick={() => handleEdit(team)}
                      className="bg-yellow-400 text-white px-5 py-2 rounded-full hover:bg-yellow-500 transition"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(team.id)}
                      className="bg-red-500 text-white px-5 py-2 rounded-full hover:bg-red-600 transition"
                    >
                      Delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

export default TeamModule;
